import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'

const easeOut = [0.22, 1, 0.36, 1] as const

const fitSignals = [
  {
    id: '01',
    title: 'We run marketing.',
    description: 'Strategy, brand, growth, delivery — one partner. We become the marketing arm of your business.',
  },
  {
    id: '02',
    title: 'ROI sets the pace.',
    description: 'Measurable outcomes only. When it works, you invest more. When it doesn\'t, we cut fast.',
  },
  {
    id: '03',
    title: 'Trust cuts both ways.',
    description: 'You try things and fail fast. We stay transparent and work as one team — no theatre.',
  },
] as const

export default function IdealFit() {
  return (
    <section
      id="fit"
      className="relative py-28 md:py-36 bg-secondary overflow-hidden"
    >
      <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-5 mix-blend-overlay pointer-events-none" />
      <div className="absolute -top-32 right-0 h-[28rem] w-[28rem] rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 xl:gap-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55, ease: easeOut }}
            className="lg:w-[38%] lg:sticky lg:top-32 lg:self-start text-center lg:text-left"
          >
            <p className="text-sm font-bold text-accent uppercase tracking-[0.2em] mb-5">
              Good Fit
            </p>
            <h2 className="text-4xl md:text-5xl xl:text-6xl font-bold font-heading text-white uppercase tracking-tight leading-[0.92] mb-6">
              Who We Work
              <br />
              <span className="text-accent">Best With.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed max-w-md mx-auto lg:mx-0 mb-8">
              Lean team. Little in-house marketing. You want a partner embedded in the
              business — or paid acquisition that actually books work.
            </p>
            <Link
              to="/new-project"
              className="inline-flex items-center gap-2 text-sm font-bold font-heading uppercase tracking-wider text-white/80 hover:text-accent transition-colors group"
            >
              See if we&apos;re a match
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <ul className="lg:flex-1 divide-y divide-white/10 border-y border-white/10">
            {fitSignals.map((signal, i) => (
              <motion.li
                key={signal.id}
                initial={{ opacity: 0, x: 36 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: easeOut }}
                className="group relative py-8 md:py-10"
              >
                <div className="absolute inset-y-0 -left-4 w-1 scale-y-0 origin-center bg-accent transition-transform duration-300 group-hover:scale-y-100 md:-left-6" />
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-3 sm:gap-8">
                  <span className="font-mono text-sm text-accent tracking-widest shrink-0">
                    {signal.id}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-2xl md:text-3xl xl:text-4xl font-bold font-heading text-white uppercase tracking-tight leading-none mb-3 transition-colors duration-300 group-hover:text-accent">
                      {signal.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed max-w-lg transition-colors duration-300 group-hover:text-slate-300">
                      {signal.description}
                    </p>
                  </div>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
